import { TFunction } from 'i18next';
import { useNavigate } from 'react-router-dom';

import { useCallback, useState } from 'react';

import { useTranslation } from '@splitfiesta/i18n';

import { useRemoveGroupMemberMutation } from '@/hooks/queries/useGroups';

import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';

import { handleApiError } from '@/utils/errorUtils';

interface UseLeaveGroupProps {
  groupId: string;
  groupName: string;
}

export function useLeaveGroup({ groupId, groupName }: UseLeaveGroupProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore(state => state.user);
  const showToast = useUIStore(state => state.showToast);
  const [isLeaving, setIsLeaving] = useState(false);

  const removeMemberMutation = useRemoveGroupMemberMutation(groupId);

  const leaveGroup = useCallback(async () => {
    if (!user?.id) return;

    // Confirmation dialog
    const confirmMessage = t('groups:leave.confirmMessage', {
      groupName,
    });

    if (!confirm(confirmMessage)) {
      return;
    }

    setIsLeaving(true);

    try {
      // Use the same API endpoint but with the current user's ID
      await removeMemberMutation.mutateAsync(user.id);

      // Show success toast
      showToast({
        type: 'success',
        message: t('groups:leave.success', { groupName }),
      });

      // Redirect to groups page
      navigate('/groups');
    } catch (error) {
      // Show server error message (e.g. "must settle up") or generic fallback
      showToast({
        type: 'error',
        message:
          handleApiError(error, t as TFunction, 'errors') ||
          t('groups:leave.error'),
      });

      setIsLeaving(false);
    }
  }, [user, groupName, t, removeMemberMutation, showToast, navigate]);

  return {
    leaveGroup,
    isLeaving,
  };
}
